// kfe-exercise-host.js — routes the right exercise screen for an item.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Exercise/ExerciseHostScreen.cs
//
// CLAUDE.md guidance: beware double-presentation when SetActive(true) and
// Route() both fire. In the browser there's no equivalent footgun — we just
// pick the right tag and mount it. Decision logic lives here so screens
// stay dumb.
//
// Dispatch buckets (Brief 037):
//   (a) Exercise — types 7, 9, 10, 11, 13, 14
//   (b) Media    — types 2, 3, 4, 5, 6, 12
//   (c) Meter    — types 16, 17, 18, 19
//   (d) Gate     — type 15 (not navigable — should be filtered from the
//                  lesson-detail list; if we ever hit one here, warn and
//                  render nothing rather than fall through to L2B)
//
// String-type routing covers CMS-shaped items (Bryan's content contract)
// that don't appear in the legacy `kfe_content_items.json`. CMS items
// carry a string `type` field; we branch on it before the numeric switch:
//   "true_false"                       → <kfe-true-false-exercise>
//   "multiple_choice"                  → <kfe-multiple-choice-exercise>
//   "video" / "video_lesson"           → <kfe-video-exercise>
//   "music_track"                      → <kfe-music-track>
//   "reading_lesson" / "reference_note"→ <kfe-html-exercise>   (Brief 045)
//
// The `default:` arm only fires for genuinely-unknown types. It logs a
// warning so the corpus survey stays honest. Type 13 (Multiple Choice —
// Method/Metaphor/Comprehension testing, 224 items) routes to
// <kfe-multiple-choice-exercise> (Brief 056); the engine + sub-type
// rendering land in Brief 058.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { persistCompletion } from "./completion-persist.js";
import { loadMeterRules, meterRuleAfterItem } from "../data/meter-rules-loader.js";
import { loadLesson } from "../data/pathway-loader.js";
import { findLessonForItem } from "../data/lesson-lookup.js";
import { navigate } from "../router.js";
import { applyMeterResult } from "../engines/meter-rule-engine.js";
import { meterDispatchAllowed, isAudioContentType } from "../data/item-completion.js";
import { ActiveTimer } from "../data/active-timer.js";
import { categoryForItem } from "../data/category-resolver.js";
import {
  ITEM_TYPE_AUDIO,
  ITEM_TYPE_VIDEO,
  ITEM_TYPE_AUDIO_HTML,
  ITEM_TYPE_PHRASE_AUDIO,
  ITEM_TYPE_HTML,
  ITEM_TYPE_HAAI,
  ITEM_TYPE_L2B_TRAINING,
  ITEM_TYPE_L2B_TESTING,
  ITEM_TYPE_SELF_RECORDING,
  ITEM_TYPE_DIALOGUE_VARIANT,
  ITEM_TYPE_MULTIPLE_CHOICE,
  ITEM_TYPE_TRUE_FALSE,
  ITEM_TYPE_GATE,
  ITEM_TYPE_METER_DIALOG_COMP,
  ITEM_TYPE_METER_LISTENING,
  ITEM_TYPE_METER_BRAINSOAKING,
  ITEM_TYPE_METER_BRAINSOAKING_ALT,
} from "../data/content-items.js";

/**
 * iOS-canonical meter dispatch (Brief 132). The meter screen is chosen STRICTLY
 * by the meter item's `master_folder_items.type`, mirroring the `switch (type)`
 * in MediaViewController.m:1768-1804 — 17 → Dialogue Comprehension (1 slider),
 * 18 → Listening Stage (6-option), 16/19 → BrainSoaking Familiarity (2 sliders;
 * 16 is iOS's "以防万一" just-in-case fallback). This is the canonical dispatch
 * key — NOT the derived `meterType` field, which exists for completion-gating
 * data only. Keyed by the numeric type constants so the map stays 1:1 with
 * content-items.js.
 */
const METER_TAG_BY_TYPE = {
  [ITEM_TYPE_METER_DIALOG_COMP]:      "kfe-dialog-comp-meter",
  [ITEM_TYPE_METER_LISTENING]:        "kfe-listening-meter",
  [ITEM_TYPE_METER_BRAINSOAKING]:     "kfe-brainsoaking-fam-meter",
  [ITEM_TYPE_METER_BRAINSOAKING_ALT]: "kfe-brainsoaking-fam-meter",
};
/**
 * Defensive fallback: meterType (meter-rules-loader) → modal UI tag. Only used
 * if the meter item is somehow absent from the content-items registry (never the
 * case in the shipped corpus — all 1441 meter rules have a content item). The
 * map is 1:1 with the type map above, so it can never disagree on a real item.
 */
const METER_TAG = {
  "dialog-comp": "kfe-dialog-comp-meter",
  "listening": "kfe-listening-meter",
  "brainsoaking": "kfe-brainsoaking-fam-meter",
};
const METER_TAGS_SELECTOR = "kfe-dialog-comp-meter, kfe-listening-meter, kfe-brainsoaking-fam-meter";

/** Pathway used when the learner has no active_pathway set (mirrors lesson-detail). */
const DEFAULT_PATHWAY_ID = "kfe_zero_to_fluency";

export class KFEExerciseHost extends KFEComponent {
  static props = ["itemId"];

  /** @type {number | string | null} */ _itemType = null;
  /** Guard: a meter is shown at most once per host view (Brief 055). */
  _meterShown = false;
  /**
   * Guard: completion is persisted at most once per host view (Brief 060b) —
   * for NON-audio items only. Audio items (types 2/5/12) bypass this guard so
   * every <audio> play-end increments the playcount (Brief 139, iOS canon
   * MediaViewController.m:1535). See _persistCompletion.
   */
  _completionPersisted = false;
  /** Brief 199: a completion write is in flight for this view. */
  _completionPending = false;
  /** Active-time accumulator for this exercise view (Brief 096). */
  /** @type {ActiveTimer | null} */ _timer = null;
  /** The lesson this exercise belongs to (active_lesson setting); null until resolved. */
  /** @type {string | null} */ _lessonId = null;
  /** Ordered (unfiltered) items of the active lesson — the meter trigger's positional context (Brief 107). */
  /** @type {Array<{ id?: string|number, type?: string, requiredPlay?: number }>} */ _lessonItems = [];
  /** Guard: active time is persisted at most once per host view (Brief 096). */
  _timeFinalised = false;

  async onConnected() {
    // Brief 096 — start the active-time clock at true mount, BEFORE the catalog
    // await, so cold-start fetch latency isn't charged as exercise time. The
    // timer pauses while the tab is hidden (Visibility API) so a walked-away
    // learner doesn't accrue idle hours, and finalises on unmount via addCleanup.
    this._timer = new ActiveTimer(() => performance.now());
    this._timer.start();
    const onVisibility = () => {
      if (!this._timer) return;
      if (document.hidden) this._timer.pause();
      else this._timer.resume();
    };
    document.addEventListener("visibilitychange", onVisibility);
    this.addCleanup(() => document.removeEventListener("visibilitychange", onVisibility));
    this.addCleanup(() => this._finaliseTime());
    // Resolve the lesson context without blocking first paint — it's needed only
    // at finalise (seconds away). Guarded: a missing/stubbed getSetting (some
    // component tests stub services.learner narrowly) or a missing setting leaves
    // _lessonId null and the finalise step simply skips persistence.
    const getSetting = /** @type {any} */ (services.learner)?.getSetting;
    if (typeof getSetting === "function") {
      this._loadLessonContext(getSetting).catch(() => {});
    }

    await services.items.loadContentItems();
    const id = Number(/** @type {any} */ (this).itemId);
    const item = Number.isFinite(id) ? services.items.getItem(id) : undefined;
    this._itemType = item ? item.type : null;
    // CMS items use a string `type` (e.g. "true_false") in addition to the
    // legacy numeric one. We branch on whichever is present.
    /** @type {any} */ const anyItem = item;
    this._itemTypeStr = typeof anyItem?.type === "string" ? anyItem.type : null;

    // Meter trigger: exercises emit a composed, bubbling `item_completed` that
    // reaches us here on the host. When the just-finished item is the affected
    // item of a meter (the iOS "preceding" case), mount that meter modally.
    //
    // Brief 060b: the same event is the completion signal for the media-style
    // exercises that emit it (audio/video/self-record). Persist completion
    // here, once per view, so those words enter the SR Auto met-pool. Screens
    // that don't emit item_completed persist via their own markItemCompleted
    // call at their completion point (l2b, haai, true-false, multiple-choice,
    // html, music-track); l2b-test persists inline. Persisting here rather
    // than re-emitting from those screens avoids newly coupling them to the
    // meter trigger above.
    const onCompleted = async () => {
      // Brief 137 + iOS canon: PERSIST the completion FIRST (mirrors
      // MediaViewController.m:1535 `incrementItemPlaycount`), THEN gate the meter
      // on the post-increment count (line 1733 `if (numberOfCompletions ==
      // requiredPlay)`). Reordered from the pre-137 meter-first sequence: the
      // gate must see THIS play-end's completion, so the write has to land before
      // _maybeShowMeter reads getItemProgress. Awaited so the read can't race the
      // write. (Pre-137 the meter fired unconditionally, so ordering was moot and
      // we ran the meter first to keep it ahead of the Brief 060b write.)
      await this._persistCompletion(id);
      await this._maybeShowMeter(id);
    };
    this.addEventListener("item_completed", onCompleted);
    this.addCleanup(() => this.removeEventListener("item_completed", onCompleted));

    this._scheduleRender();

    // Meters are not navigable; they fire modally from here when a sibling item
    // completes (Brief 055). Load the rule map in the background — it's only
    // needed once item_completed fires (seconds away), so it must not block the
    // exercise's first paint. _maybeShowMeter degrades gracefully if it's not
    // loaded yet. Idempotent; falls back to {} if the file is absent.
    loadMeterRules().catch(() => {});
  }

  /**
   * Resolve the active lesson context without blocking first paint: the lesson id
   * (for active-time attribution, Brief 096) and the ordered lesson items (for the
   * positional meter trigger, Brief 107). Both are needed only once item_completed
   * fires, seconds away, so a slow lesson fetch never delays the exercise. Fully
   * guarded — any failure leaves _lessonItems empty and _maybeShowMeter no-ops.
   * @param {Function} getSetting services.learner.getSetting (already type-checked).
   */
  async _loadLessonContext(getSetting) {
    const [activeLesson, activePathway] = await Promise.all([
      Promise.resolve(getSetting.call(services.learner, "active_lesson")),
      Promise.resolve(getSetting.call(services.learner, "active_pathway")).catch(() => null),
    ]);
    this._lessonId = activeLesson ? String(activeLesson) : null;
    let pathwayId = activePathway ? String(activePathway) : null;

    // Deep-link path (Brief 145, mirroring Brief 142's screen-side fix): the
    // learner arrived via a shared/bookmarked `/exercise/<id>` URL, so
    // lesson-detail never wrote `active_lesson`. Without a lesson the positional
    // meter lookup (_maybeShowMeter) has no _lessonItems to scan and silently
    // no-ops even when the gate condition is met. Reverse-derive the owning
    // lesson from the item id so the meter dispatch works on the deep link too.
    // Read-only — the next happy-path entry still writes the setting (Brief 145
    // keeps lesson-detail the single writer, same trade-off as Brief 142).
    if (!this._lessonId) {
      const id = Number(/** @type {any} */ (this).itemId);
      if (Number.isFinite(id)) {
        const derived = await findLessonForItem(id);
        if (derived) {
          this._lessonId = derived.lessonId;
          if (!pathwayId) pathwayId = derived.pathwayId;
        }
      }
    }

    if (!this._lessonId) return;
    if (!pathwayId) pathwayId = DEFAULT_PATHWAY_ID;
    try {
      const lesson = /** @type {any} */ (await loadLesson(pathwayId, this._lessonId));
      this._lessonItems = Array.isArray(lesson?.items) ? lesson.items : [];
    } catch (err) {
      console.warn("[exercise-host] lesson load for meter trigger failed", err);
    }
  }

  /**
   * Persist that this item was completed (Brief 060b). Returns the write promise
   * (Brief 137) so onCompleted can await it before the meter gate reads the
   * post-increment count — but stays guarded so a failed write only means the
   * words don't enter the met-pool this session.
   *
   * Brief 139 — type-aware guard relax. The one-shot guard exists because most
   * exercise screens (l2b, haai, true-false, multiple-choice, html, music-track)
   * emit `item_completed` EXACTLY ONCE at their internal completion point, and
   * the host promised them "I will not double-write." Audio screens are the
   * divergence: <kfe-audio-exercise> emits `item_completed` on every <audio>
   * `ended`, and under iOS canon each play-end is a legitimate increment
   * (MediaViewController.m:1535 `incrementItemPlaycount`, no per-view guard). So
   * audio item types (2/5/12) fall through unconditionally — every play-end
   * advances `completedCount`; the Brief 137 meter gate downstream sees the live
   * count and fires once it reaches `requiredPlay`. Every non-audio type keeps
   * the single-write-per-view safety net.
   * @param {number} completedItemId
   * @returns {Promise<void>}
   */
  async _persistCompletion(completedItemId) {
    if (!Number.isFinite(completedItemId)) return;
    const isAudio = isAudioContentType(this._itemType);
    if (!isAudio) {
      // Brief 199, criterion 3: the latch is set only once the write has
      // landed, and a failure leaves it clear so a later `item_completed` can
      // try again. `_completionPending` holds the second event of the same
      // lifecycle while the first write is in flight — learner-store shares the
      // in-flight promise too, so neither path can double-count (`completedCount`
      // is a counter, not a flag — Brief 072).
      if (this._completionPersisted || this._completionPending) return;
      this._completionPending = true;
      const ok = await persistCompletion(completedItemId);
      this._completionPending = false;
      this._completionPersisted = ok;
      return;
    }
    // Audio types: every play-end is its own legitimate increment, so there is
    // no latch to set — but the write is still awaited and retried, and a
    // failure still reaches the learner (via persistCompletion).
    await persistCompletion(completedItemId);
  }

  /**
   * Finalise active-time tracking (Brief 096). Runs once, on unmount: flushes the
   * visibility-paused active duration and attributes it to the lesson, bucketed by
   * the item's Category letter (B–L; uncategorised time counts toward total only).
   * Fire-and-forget + fully guarded — instrumentation must never break teardown.
   */
  _finaliseTime() {
    if (this._timeFinalised) return;
    this._timeFinalised = true;
    try {
      const ms = this._timer ? this._timer.stop() : 0;
      if (!(ms > 0) || !this._lessonId) return;
      const itemId = Number(/** @type {any} */ (this).itemId);
      const category = Number.isFinite(itemId) ? categoryForItem(itemId) : null;
      services.learner
        .addLessonTime(this._lessonId, ms, category)
        .catch((err) => console.warn("[exercise-host] addLessonTime failed", err));
    } catch (err) {
      console.warn("[exercise-host] _finaliseTime failed", err);
    }
  }

  /**
   * Mount the meter that immediately follows the just-completed item in the
   * lesson sequence, if any (Brief 107 — positional trigger). The meter's own
   * lesson position is the trigger; its affectedItemId/direction is only the
   * playcount target, so it must not be used to decide which meter fires.
   *
   * Both meter functions auto-present (Brief 112). iOS canon
   * (MediaViewController.m:1753-1808) presents the meter that sits at the next
   * lesson position purely positionally — `fi.type in (16,17,18,19) and
   * fi.item_order = itemOrder + 1` — with NO affectedItemId / function check. The
   * meter VC is shown and answered regardless; the Function-A vs B split is
   * database-side only (KFEMetersDatabaseApi.m always records the answer, then
   * bumps required_play only for Dialog/Listening WITH an affected item). The web
   * mirrors this: the engine's `applyMeterResult` dispatches A (playcount) vs B
   * (telemetry-only) on completion — see _onMeterComplete — so the same modal UI
   * serves both. Pre-112 this guarded out Function-B (null affectedItemId)
   * meters, leaving 837 corpus meters (401 BrainSoaking + 436 Dialog templates)
   * never presented; that was the Brief 107 §Out-of-scope deferral.
   *
   * Brief 137 — completion gate: the meter dispatches only once the just-completed
   * predecessor has been played to its full `resolveRequiredPlay` count (iOS
   * MediaViewController.m:1733). Every earlier play-end is a no-op. The gate runs
   * AFTER the positional rule lookup — a cheap array scan with no side effect —
   * so the (async) progress read happens only when a meter would actually fire,
   * not on every item_completed; the learner-visible contract is identical.
   * @param {number} completedItemId
   * @returns {Promise<void>}
   */
  async _maybeShowMeter(completedItemId) {
    if (this._meterShown || !Number.isFinite(completedItemId)) return;
    let rule;
    try {
      rule = meterRuleAfterItem(this._lessonItems, completedItemId);
    } catch {
      return; // rules not loaded — skip silently
    }
    if (!rule) return;

    // Brief 137 completion gate. iOS fires the meter only at numberOfCompletions
    // == required_play; mirror with a defensive >= over the post-increment count.
    // The authored requiredPlay comes from the lesson item the host already holds
    // (_authoredRequiredPlayFor); the per-learner bump comes from itemProgress.
    // resolveRequiredPlay reconciles them as max(authored, bumped) (Brief 125), so
    // a bump from an earlier meter isn't masked. Pedagogy gate (Rule #8) — never
    // relaxed by dev-mode navigation gating.
    let progress;
    try {
      progress = await services.learner.getItemProgress?.(completedItemId);
    } catch {
      progress = undefined;
    }
    const authoredRequiredPlay = this._authoredRequiredPlayFor(completedItemId);
    if (!meterDispatchAllowed(progress?.completedCount ?? 0, { requiredPlay: authoredRequiredPlay }, progress?.requiredPlay)) {
      return; // predecessor not yet fully played — defer to a future play-end
    }
    if (this._meterShown) return; // re-check after the await (concurrent completions)
    // Dispatch STRICTLY by the meter item's content type (iOS canon — the
    // MediaViewController.m:1768-1804 switch on master_folder_items.type), NOT by
    // the derived rule.meterType. The positional trigger already identified the
    // meter item; rule.itemId is its id (stamped at load). Fall back to the
    // meterType map only if the content item is absent (never in the corpus).
    const meterItem = services.items.getItem(rule.itemId);
    const tag =
      (meterItem && METER_TAG_BY_TYPE[meterItem.type]) || METER_TAG[rule.meterType];
    if (!tag) return;

    this._meterShown = true;
    const meter = document.createElement(tag);
    /** @type {any} */ (meter).meterRule = rule;
    meter.addEventListener(
      "kfe:meter-complete",
      // Brief 185: completedItemId is the preceding content that triggered the
      // meter (Brief 107 positional model) — the iOS `measured_item_id` link.
      (e) => this._onMeterComplete(rule, /** @type {CustomEvent} */ (e).detail, undefined, completedItemId),
      { once: true },
    );
    this.shadowRoot?.appendChild(meter);
    this.addCleanup(() => meter.remove());
  }

  /**
   * The authored requiredPlay of a lesson item, read from the ordered lesson
   * items the host already holds for the positional meter trigger (_lessonItems).
   * Lesson item ids are prefixed strings ("item_103880"); the affected id is the
   * bare number. Returns 0 when the item is absent or carries no numeric value —
   * the bump then falls back to its stored/completed base (Brief 125).
   * @param {number|null|undefined} affectedItemId
   * @returns {number}
   */
  _authoredRequiredPlayFor(affectedItemId) {
    if (affectedItemId == null) return 0;
    const target = Number(affectedItemId);
    for (const it of this._lessonItems) {
      const numId = Number(String(it?.id ?? "").replace(/^(item_|gate_)/, ""));
      if (numId === target && typeof it?.requiredPlay === "number") return it.requiredPlay;
    }
    return 0;
  }

  /**
   * Apply the meter result via the pure engine (it dispatches Function A vs B on
   * affectedItemId), dismiss the modal, and navigate per the iOS-canon button
   * intent (Brief 125):
   *   • detail.goToItem (learner tapped "Listen to … again" on a preceding-fail)
   *     → navigate to the affected item so it replays toward the bumped
   *     requiredPlay — iOS kMeterGoToItemNotification (KFEListeningMeterVC.m:127).
   *   • otherwise (pass / "go back to list" / following / Function B) → return to
   *     the lesson surface, which re-mounts and re-evaluates gating against the
   *     adjusted playcount — iOS kMeterBackToListNotification.
   * @param {import("../data/meter-rules-loader.js").MeterRule} rule
   * @param {{ answer1: number, answer2: number | null, goToItem?: boolean }} detail
   * @param {import("../engines/meter-rule-engine.js").MeterResultDeps} [deps]
   *   side-effect injection seam (Brief 112 test seam); defaults to the live
   *   learner-store so production callers (the kfe:meter-complete listener) pass nothing.
   * @param {number | null} [measuredItemId] the preceding completed content that
   *   triggered the meter (iOS measured_item_id, Brief 185). Recorded on the reading.
   */
  async _onMeterComplete(rule, detail, deps, measuredItemId = null) {
    // Brief 125: seed the playcount bump from the affected item's AUTHORED
    // requiredPlay (Brief 100, shipped on the lesson item) so +delta lands on
    // the designer's baseline — iOS's single required_play column starts there.
    // The affected item is a lesson sibling of the meter (direction 0/2 =
    // preceding, 1/3 = following), so it lives in _lessonItems; 0 when absent.
    const authoredRequiredPlay = this._authoredRequiredPlayFor(rule?.affectedItemId);
    const sideEffects = deps ?? {
      applyPlaycountAdjustment: (itemId, delta) =>
        services.learner.applyPlaycountAdjustment(itemId, delta, authoredRequiredPlay),
      recordMeterTelemetry: services.learner.recordMeterTelemetry,
    };
    try {
      await applyMeterResult(rule, detail.answer1, detail.answer2, sideEffects, measuredItemId);
    } catch (err) {
      console.warn("[exercise-host] applyMeterResult failed", err);
    }
    this.shadowRoot?.querySelectorAll(METER_TAGS_SELECTOR).forEach((n) => n.remove());
    if (detail.goToItem && rule && rule.affectedItemId != null) {
      navigate(`/exercise/${rule.affectedItemId}`);
    } else {
      history.back();
    }
  }

  render() {
    const id = Number(/** @type {any} */ (this).itemId);
    const type = this._itemType;
    const typeStr = this._itemTypeStr;
    if (type == null && typeStr == null) return html`<p>${t("loading")}</p>`;

    /** @type {string} */ let tag;
    // CMS string-type routing runs first — these items carry a string `type`
    // (Bryan's content contract) and don't appear in the legacy numeric
    // switch below.
    if (typeStr != null) {
      switch (typeStr) {
        case "true_false":      tag = "kfe-true-false-exercise";      break;
        case "multiple_choice": tag = "kfe-multiple-choice-exercise"; break;
        case "video":           tag = "kfe-video-exercise";           break;
        case "video_lesson":    tag = "kfe-video-exercise";           break;
        case "music_track":     tag = "kfe-music-track";              break;
        case "reading_lesson":  tag = "kfe-html-exercise";            break;
        case "reference_note":  tag = "kfe-html-exercise";            break;
        default:
          console.warn(`[exercise-host] unknown string type ${typeStr} for id ${id}`);
          tag = "kfe-l2b-exercise";
          break;
      }
    } else {
      switch (type) {
        // (a) Exercises
        case ITEM_TYPE_HAAI:                   tag = "kfe-haai-exercise";          break;
        case ITEM_TYPE_L2B_TRAINING:           tag = "kfe-l2b-exercise";           break;
        case ITEM_TYPE_L2B_TESTING:            tag = "kfe-l2b-test-exercise";      break;
        case ITEM_TYPE_SELF_RECORDING:         tag = "kfe-self-record";            break;
        case ITEM_TYPE_MULTIPLE_CHOICE:        tag = "kfe-multiple-choice-exercise"; break;
        case ITEM_TYPE_TRUE_FALSE:             tag = "kfe-true-false-exercise";    break;
        // (b) Media — passive playback / reading
        case ITEM_TYPE_AUDIO:                  tag = "kfe-audio-exercise";         break;
        case ITEM_TYPE_VIDEO:                  tag = "kfe-video-exercise";         break;
        case ITEM_TYPE_AUDIO_HTML:             tag = "kfe-html-exercise";          break;
        case ITEM_TYPE_PHRASE_AUDIO:           tag = "kfe-audio-exercise";         break;
        case ITEM_TYPE_HTML:                   tag = "kfe-html-exercise";          break;
        case ITEM_TYPE_DIALOGUE_VARIANT:       tag = "kfe-audio-exercise";         break;
        // (c) Meters — NOT navigable as menu items (Brief 055). They fire
        // modally from this host when a sibling item completes (_maybeShowMeter)
        // and are filtered out of the lesson-detail list. If a meter id is
        // navigated to directly, render nothing rather than fall through to L2B.
        case ITEM_TYPE_METER_DIALOG_COMP:
        case ITEM_TYPE_METER_LISTENING:
        case ITEM_TYPE_METER_BRAINSOAKING:
        case ITEM_TYPE_METER_BRAINSOAKING_ALT:
          console.warn(`[exercise-host] meter item ${id} is not navigable; meters fire modally on sibling completion`);
          return html`<p>${t("loading")}</p>`;
        // (d) Gates — never navigable. Filtering happens in lesson-detail;
        // if a gate ID ever lands here directly, render nothing rather than
        // silently falling through to the L2B engine (which would report
        // "0 mastered" with no MMUs).
        case ITEM_TYPE_GATE:
          console.warn(`[exercise-host] gate item ${id} should not be navigable`);
          return html`<p>${t("loading")}</p>`;
        default:
          console.warn(`[exercise-host] unknown item type ${type} for id ${id}`);
          tag = "kfe-l2b-exercise";
          break;
      }
    }

    const child = document.createElement(tag);
    /** @type {any} */ (child).itemId = id;
    return html`${child}`;
  }
}
